import "@/index.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";
import { DashaPhoto } from "@/components/DashaPhoto";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Thanks from "@/pages/Thanks";

const STRIPE_URL = "https://buy.stripe.com/8x200jdCbaqG0KPePt0co0D";

const CtaButton = ({ testId, className = "" }) => (
  <a
    data-testid={testId}
    href={STRIPE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`cta-btn rounded-full px-8 py-4 text-base sm:text-lg ${className}`}
  >
    Get Alignment — $27
    <ArrowUpRight className="h-5 w-5" />
  </a>
);

const topics = [
  {
    n: "01",
    title: "Decisions",
    sub: "Trust your process",
    copy: "Reflect on how you make decisions and experiment with a process that feels more like your own.",
  },
  {
    n: "02",
    title: "Energy",
    sub: "Find your rhythm",
    copy: "Notice the way you use your energy and explore a working rhythm you can actually sustain.",
  },
  {
    n: "03",
    title: "Expression",
    sub: "Use your voice",
    copy: "Bring your perspective into your offers, communication and marketing instead of copying someone else's.",
  },
];

const Hero = () => (
  <header data-testid="hero-section" className="bg-ink px-5 pt-6 sm:px-8 sm:pt-10">
    <div className="mx-auto max-w-3xl">
      <p className="reveal reveal-1 mt-2 pt-2 text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-cream/70 sm:text-xs">
        For entrepreneurs ready to stop forcing their business
      </p>

      <h1
        data-testid="hero-headline"
        className="reveal reveal-2 mt-4 text-center font-condensed text-[2rem] font-semibold uppercase leading-[1.08] tracking-tight text-cream sm:text-5xl sm:leading-[1.05]"
      >
        What if you're not bad at business…{" "}
        <span className="font-serif-accent normal-case italic tracking-normal text-lime">
          you're just building it like someone else?
        </span>
      </h1>

      <p className="reveal reveal-3 mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed text-cream/75 sm:text-base">
        Discover a different way to make decisions, create offers, communicate, market and
        work — based on how YOU naturally operate.
      </p>

      <div className="reveal reveal-3 mt-6">
        <img
          data-testid="hero-bundle-photo"
          src="/images/alignment-bundle.jpg"
          alt="Dasha Love with the Quantum Codes book and the ALIGNMENT digital bundle on laptop and phone"
          className="w-full rounded-xl"
        />
      </div>

      <div className="reveal reveal-3 mt-6">
        <p className="mb-3 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-cream/60">
          <Play className="h-3 w-3 fill-current text-lime" />
          A different way to build. A minute with Dasha.
        </p>
        <VideoPlayer />
      </div>

      <div className="reveal reveal-4 mt-6 flex flex-col items-center gap-2 pb-12 sm:pb-16">
        <CtaButton testId="hero-cta-button" className="w-full sm:w-auto" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">
          Human Design for Business + Digital Playbook
        </span>
      </div>
    </div>
  </header>
);

const Statement = () => (
  <section data-testid="statement-section" className="bg-ink px-5 py-16 sm:px-8 sm:py-24">
    <div className="mx-auto max-w-4xl text-center">
      <h2 className="font-condensed text-4xl font-bold uppercase leading-[1.02] tracking-tight text-cream sm:text-6xl">
        Stop copying everybody else's blueprint.
        <br />
        <span className="text-lime">Start getting curious about your own.</span>
      </h2>
      <p className="mx-auto mt-12 max-w-2xl font-condensed text-xl font-semibold uppercase leading-snug tracking-wide text-cream/85 sm:text-3xl">
        Your decisions. Your energy. Your voice. Your business.
      </p>
    </div>
  </section>
);

const Playbook = () => (
  <section data-testid="playbook-section" className="bg-ink px-5 py-16 sm:px-8 sm:py-24">
    <div className="mx-auto max-w-4xl">
      <div className="grid items-center gap-8 sm:grid-cols-2 sm:gap-12">
        <DashaPhoto className="w-full rounded-lg" testId="quantum-codes-photo" />
        <div className="text-center sm:text-left">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-lime">
            The $27 starter bundle
          </p>
          <h2 className="mt-3 font-condensed text-3xl font-semibold uppercase leading-[1.05] tracking-tight text-cream sm:text-4xl">
            Get Alignment. Get the book.{" "}
            <span className="font-serif-accent normal-case italic text-lime">Build like you.</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-cream/70">
            A practical introduction to Human Design as a framework for self-reflection and
            experimentation in your business.
          </p>
          <div className="mt-6 space-y-3 text-left">
            <div className="border border-cream/15 p-4">
              <p className="font-condensed text-sm font-semibold uppercase tracking-wide text-cream">
                Alignment — Human Design for Business
              </p>
            </div>
            <div className="border border-cream/15 p-4">
              <p className="font-condensed text-sm font-semibold uppercase tracking-wide text-cream">
                Quantum Codes: Human Design &amp; Gene Keys Playbook
              </p>
              <p className="mt-1 text-xs text-cream/50">Digital edition included</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-cream/70">
            Explore decisions, energy, communication, offers and marketing through a more personal lens.
          </p>
          <div className="mt-6 flex flex-col items-center gap-2 sm:items-start">
            <CtaButton testId="bundle-cta-button" className="w-full sm:w-auto" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">
              Digital bundle · No physical book shipped
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeelLikeYou = () => (
  <section data-testid="feel-like-you-section" className="bg-cream px-5 py-16 text-ink sm:px-8 sm:py-24">
    <div className="mx-auto max-w-3xl">
      <h2 className="font-condensed text-3xl font-semibold uppercase leading-[1.05] tracking-tight sm:text-5xl">
        Your business should <span className="font-serif-accent normal-case italic">feel like you.</span>
      </h2>
      <p className="mt-6 font-condensed text-xl font-semibold uppercase leading-snug tracking-tight sm:text-2xl">
        You've learned the strategies. Now get to know the person using them.
      </p>
      <p className="mt-6 text-sm leading-relaxed text-ink/75 sm:text-base">
        How often to post. What offer to create. How hard to work. When to launch. There is no
        shortage of advice. ALIGNMENT gives you space to explore what fits, what feels forced, and
        what you want to try differently.
      </p>
    </div>
  </section>
);

const Testimonials = ({ images = [1, 2, 3, 4, 5, 6, 7, 8, 9], testId = "testimonials-section", heading = true }) => (
  <section data-testid={testId} className="overflow-hidden bg-ink px-5 py-16 sm:px-8 sm:py-24">
    <div className="mx-auto max-w-5xl">
      {heading && (
        <>
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">
            Real messages
          </p>
          <h2 className="mt-3 text-center font-condensed text-3xl font-semibold uppercase leading-[1.05] tracking-tight text-cream sm:text-5xl">
            This is what happens <span className="font-serif-accent normal-case italic text-lime">when it clicks.</span>
          </h2>
        </>
      )}

      <div className={`${heading ? "mt-12" : ""} columns-2 gap-3 sm:columns-3 sm:gap-4`}>
        {images.map((n) => (
          <img
            key={n}
            data-testid={`${testId}-image-${n}`}
            src={`/images/testi-${n}.jpg`}
            alt="Client message screenshot"
            loading="lazy"
            className={`mb-3 w-full rounded-lg shadow-lg transition-transform duration-300 hover:rotate-0 hover:scale-[1.03] sm:mb-4 ${
              n % 3 === 1 ? "-rotate-1" : n % 3 === 2 ? "rotate-1" : "-rotate-[0.5deg]"
            }`}
          />
        ))}
      </div>
    </div>
  </section>
);

const Explore = () => (
  <section data-testid="explore-section" className="border-t border-ink/10 bg-cream px-5 py-16 text-ink sm:px-8 sm:py-24">
    <div className="mx-auto max-w-5xl">
      <div className="grid gap-4 sm:grid-cols-3">
        {topics.map((t) => (
          <div
            key={t.n}
            data-testid={`explore-card-${t.title.toLowerCase()}`}
            className="group border border-ink/15 bg-cream p-6 transition-colors duration-300 hover:bg-ink"
          >
            <span className="font-condensed text-sm font-semibold tracking-[0.25em] text-ink/40 transition-colors duration-300 group-hover:text-lime">
              {t.n} / {t.title}
            </span>
            <h3 className="mt-3 font-condensed text-2xl font-semibold uppercase tracking-tight transition-colors duration-300 group-hover:text-cream">
              {t.sub}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/70 transition-colors duration-300 group-hover:text-cream/70">
              {t.copy}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <CtaButton testId="explore-cta-button" className="w-full sm:w-auto" />
      </div>
    </div>
  </section>
);

const LessForcing = () => (
  <section data-testid="less-forcing-section" className="bg-ink px-5 py-16 sm:px-8 sm:py-24">
    <div className="mx-auto max-w-3xl">
      <h2 className="font-condensed text-4xl font-bold uppercase leading-[1.0] tracking-tight text-cream sm:text-6xl">
        Less forcing.
        <br />
        More noticing.
        <br />
        <span className="text-lime">More you.</span>
      </h2>
      <div className="mt-8 space-y-4 text-sm leading-relaxed text-cream/70 sm:text-base">
        <p>You don't need to become someone else to build something meaningful.</p>
        <p>
          Use Human Design as a starting point for curiosity: notice your patterns, test a
          different approach, and keep what helps you move forward.
        </p>
        <p className="text-cream/50">
          No Human Design experience needed. Bring your business, your ideas, and a willingness to
          experiment.
        </p>
      </div>
    </div>
  </section>
);

const NextStep = () => (
  <section data-testid="next-step-section" className="bg-ink px-5 py-16 text-center sm:px-8 sm:py-24">
    <div className="mx-auto max-w-4xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">
        Your next step
      </p>
      <h2 className="mt-4 font-condensed text-5xl font-bold uppercase leading-[0.95] tracking-tight text-cream sm:text-7xl">
        Stop copying.
        <br />
        <span className="text-lime">Start experimenting.</span>
        <br />
        Build like you.
      </h2>
    </div>
  </section>
);

const faqs = [
  {
    q: "What's included?",
    a: "ALIGNMENT — Human Design for Business, bundled with the digital edition of Quantum Codes: Human Design & Gene Keys Playbook.",
  },
  {
    q: "Do I need to understand Human Design already?",
    a: "No. ALIGNMENT is an introduction designed to help you reflect on decisions, energy, communication, offers and your way of working.",
  },
  {
    q: "Will I receive a physical book?",
    a: "No. This $27 bundle includes the digital edition of the playbook. No physical book is shipped.",
  },
  {
    q: "Is this a scientific assessment or a promise of business results?",
    a: "No. Human Design and Gene Keys are used here as self-reflection and experimentation frameworks, not validated scientific assessments. Business results are not guaranteed.",
  },
];

const Faq = () => (
  <section data-testid="faq-section" className="bg-cream px-5 pb-16 text-ink sm:px-8 sm:pb-24">
    <div className="mx-auto max-w-2xl space-y-6 border-t border-ink/15 pt-12">
      {faqs.map((f, i) => (
        <div key={i} data-testid={`faq-item-${i}`}>
          <h3 className="font-condensed text-base font-semibold uppercase tracking-wide">{f.q}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink/70">{f.a}</p>
        </div>
      ))}
    </div>
  </section>
);

const FinalOffer = () => (
  <section data-testid="final-offer-section" className="bg-cream px-5 pt-16 text-center text-ink sm:px-8 sm:pt-24">
    <div className="mx-auto max-w-2xl">
      <p className="font-condensed text-2xl font-semibold uppercase tracking-[0.25em]">Alignment</p>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink/50">
        Human Design for Business
      </p>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink/50">
        + Quantum Codes digital playbook
      </p>

      <p data-testid="final-price" className="mt-8 font-condensed text-7xl font-bold leading-none tracking-tight sm:text-8xl">
        $27
      </p>
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink/50">
        One bundle. One payment.
      </p>

      <div className="mt-8">
        <CtaButton testId="final-cta-button" className="w-full sm:w-auto" />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="footer" className="bg-ink px-5 pb-28 pt-10 sm:px-8 md:pb-12">
    <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/70">
        Alignment © 2026 Dasha Love
      </p>
      <p className="text-[11px] leading-relaxed text-cream/40">
        For self-reflection and experimentation.
      </p>
      <div className="flex gap-6">
        <Link data-testid="privacy-link" to="/privacy" className="text-[11px] uppercase tracking-[0.2em] text-cream/50 underline-offset-4 hover:text-lime hover:underline">
          Privacy Policy
        </Link>
        <Link data-testid="terms-link" to="/terms" className="text-[11px] uppercase tracking-[0.2em] text-cream/50 underline-offset-4 hover:text-lime hover:underline">
          Terms
        </Link>
      </div>
    </div>
  </footer>
);

const StickyCta = () => (
  <div
    data-testid="sticky-cta-bar"
    className="fixed inset-x-0 bottom-0 z-50 border-t border-lime/30 bg-ink/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md md:hidden"
  >
    <a
      data-testid="sticky-cta-button"
      href={STRIPE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="cta-btn w-full rounded-full px-6 py-3.5 text-base"
    >
      Get Alignment — $27
      <ArrowUpRight className="h-5 w-5" />
    </a>
  </div>
);

const LandingPage = () => (
  <div className="grain min-h-screen bg-ink text-cream">
    <main>
      <Hero />
      <Statement />
      <Playbook />
      <FeelLikeYou />
      <Explore />
      <LessForcing />
      <Testimonials />
      <NextStep />
      <FinalOffer />
      <Faq />
    </main>
    <Footer />
    <StickyCta />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/thanks" element={<Thanks />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
